import { FC } from 'react';
import { WidgetCell, WidgetProps } from '../WidgetCell';
import { useIntl } from 'react-intl';
import { useDrawerContext } from '../../../../../drawer/DrawerContext';
import { Box, Stack } from '@mui/material';
import { AddOpportunitySelect } from './AddOpportunitySelect';
import { graphql } from '../../../../../gql';
import { useApiMutation } from '../../../../../query/hooks';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { z } from 'zod';
import { opportunityEditDrawerSchema } from './OpportunityEditDrawer';
import { useTypedQueryParams } from '../../../../../hooks/useTypedQueryParams';
import { CellCard } from '../../components/CellCard';
import { Bolt } from '@mui/icons-material';
import slugify from 'slugify';
import { widgetDefinitions } from '../widgetDefinitions';
import { useSnackbar } from '../../../../../store/snackbar';

const opportunitiesWidget_addOpportunity = graphql(`
	mutation opportunitiesWidget_addOpportunity($activityId: UUID!, $opportunityId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { opportunities: { connect: { id: $opportunityId } } }) {
			ok
			errorMessage
		}
	}
`);

const opportunitiesWidget_removeOpportunity = graphql(`
	mutation opportunitiesWidget_removeOpportunity($activityId: UUID!, $opportunityId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { opportunities: { disconnect: { id: $opportunityId } } }) {
			ok
			errorMessage
		}
	}
`);

const drawerSchema = z.object({
	drawer: z.union([opportunityEditDrawerSchema, z.object({})]).optional(),
});

export const OpportunitiesCell: FC<WidgetProps> = ({ widget: { opportunities }, activityId, activityName }) => {
	const intl = useIntl();
	const { setDrawerWithoutPrevParams } = useDrawerContext();
	const { openSnackbar } = useSnackbar();
	const { mutate: addOpportunity } = useApiMutation(opportunitiesWidget_addOpportunity, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'journey.detail.widget.opportunities.add.success' }),
					error: intl.formatMessage({ id: 'journey.detail.widget.opportunities.add.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			});
		},
	});
	const { mutate: removeOpportunity, isPending: isRemovePending } = useApiMutation(opportunitiesWidget_removeOpportunity, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'journey.detail.widget.opportunities.remove.success' }),
					error: intl.formatMessage({ id: 'journey.detail.widget.opportunities.remove.error' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			});
		},
	});

	const handleAddOpportunity = (opportunityId: string) => {
		addOpportunity({
			activityId,
			opportunityId,
		});
	};

	const handleOpenDrawer = (opportunityId: string, activityId: string) => {
		setDrawerWithoutPrevParams('editOpportunity', { opportunityId, activityId });
	};

	const handleRemoveOpportunity = (opportunityId: string) => {
		removeOpportunity({
			activityId,
			opportunityId,
		});
	};

	const [{ drawer }] = useTypedQueryParams(drawerSchema);
	let opportunityId = null as string | null;
	if (drawer && typeof drawer === 'object' && 'opportunityId' in drawer) {
		opportunityId = drawer.opportunityId;
	}

	return (
		<WidgetCell
			data-activity-widget-name={slugify(activityName, { lower: true })}
			data-activity-widget-type={widgetDefinitions.opportunities.type}
			sx={{
				'& .add-opportunity-button': { opacity: 0 },
				'&:hover .add-opportunity-button': { opacity: 1 },
				'&:focus-within .add-opportunity-button': { opacity: 1 },
			}}
		>
			<Stack direction={'column'} gap={1.5} alignItems={'stretch'}>
				{opportunities.map((o) => (
					<CellCard
						key={o.id}
						name={o.name}
						description={o.description ?? ''}
						onEdit={() => handleOpenDrawer(o.id, activityId)}
						onRemove={() => handleRemoveOpportunity(o.id)}
						icon={(props) => <Bolt {...props} />}
						isActive={o.id === opportunityId}
						disabled={isRemovePending}
					/>
				))}
				<Box className={'add-opportunity-button'}>
					<AddOpportunitySelect removedIds={opportunities.map((o) => o.id)} onChange={handleAddOpportunity} activityId={activityId} />
				</Box>
			</Stack>
		</WidgetCell>
	);
};
